/**
 * Amortigua lo que sale por el puerto antes de dárselo al monitor serie.
 *
 * Hace falta porque el monitor pinta cada mensaje con un setState, y un sketch
 * que imprime dentro del loop sin ningún delay manda todo lo que el puerto
 * aguante. En un ESP32 con USB nativo el baudrate no frena nada -- los 9600 son
 * una formalidad, el USB va a su propia velocidad -- así que son cientos de miles
 * de caracteres por segundo. Un mensaje por cada pedazo que llega deja a la
 * pestaña sin main thread: el monitor no alcanza ni a abrirse.
 *
 * Entonces se junta y se entrega a ritmo fijo. Y se recorta: el monitor se queda
 * con los últimos caracteres de todas formas, así que guardar más de lo que
 * entra en su ventana es trabajo tirado.
 */

export const DEFAULT_FLUSH_MS = 150;
// Alcanza para cubrir la ventana que muestra el monitor (5000 caracteres) sin
// arrastrar de más entre entrega y entrega.
export const DEFAULT_MAX_BUFFER = 4000;

class MonitorFeed {
  constructor(emit, { flushMs = DEFAULT_FLUSH_MS, maxBuffer = DEFAULT_MAX_BUFFER, now = () => new Date() } = {}) {
    this.emit = emit;
    this.flushMs = flushMs;
    this.maxBuffer = maxBuffer;
    this.now = now;
    this.buffer = '';
    this.timer = null;
    // Lo que se descartó por recorte, para poder decir que hubo un salto.
    this.dropped = 0;
  }

  push = (text) => {
    if (!text) return;
    this.buffer += text;
    if (this.buffer.length > this.maxBuffer) {
      this.dropped += this.buffer.length - this.maxBuffer;
      this.buffer = this.buffer.slice(-this.maxBuffer);
    }
    // El primero que llega agenda la entrega; los que siguen se suman a la misma.
    if (!this.timer) this.timer = setTimeout(this.flush, this.flushMs);
  }

  flush = () => {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.buffer) return;
    const message = this.buffer;
    this.buffer = '';
    this.dropped = 0;
    const date = this.now();
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`;
    this.emit({ time, message });
  }

  // Corta la entrega pendiente y tira lo acumulado: el monitor se está cerrando y
  // eso ya no le interesa a nadie.
  stop = () => {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.buffer = '';
    this.dropped = 0;
  }
}

export default MonitorFeed;
